
import React, { useState, useEffect } from 'react';
import { Lesson } from '../types';

interface LessonViewProps {
  lesson: Lesson | null;
  onGenerate: () => void;
  onToggleFavorite: (id: string) => void;
  onUpdateNotes: (id: string, notes: string) => void;
  isLoading: boolean;
  t: any;
}

const LessonView: React.FC<LessonViewProps> = ({ lesson, onGenerate, onToggleFavorite, onUpdateNotes, isLoading, t }) => {
  const [localNotes, setLocalNotes] = useState(lesson?.notes || "");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setLocalNotes(lesson?.notes || "");
  }, [lesson]);

  const handleSaveNotes = () => {
    if (!lesson) return;
    setIsSaving(true);
    onUpdateNotes(lesson.id, localNotes);
    setTimeout(() => setIsSaving(false), 500);
  };

  const handleShare = async () => {
    if (!lesson) return;
    
    const shareText = `${t.shareText}\n\n"${lesson.title}"\n${lesson.source} - ${lesson.reference}\n\n${lesson.principle}\n\nAction: ${lesson.action}`;
    
    // Validate current URL before sharing. Sandboxed environments often use 'about:srcdoc' which is an invalid URL for navigator.share
    const currentUrl = window.location.href;
    const isValidUrl = currentUrl.startsWith('http');

    const shareData: ShareData = {
      title: lesson.title,
      text: shareText,
      ...(isValidUrl ? { url: currentUrl } : {})
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        throw new Error('Native share not available or invalid data');
      }
    } catch (err) {
      // Fallback to clipboard for all errors (including 'Invalid URL')
      try {
        await navigator.clipboard.writeText(shareText + (isValidUrl ? `\n\n${currentUrl}` : ''));
        alert(t.noteSaved); // Use existing translation for confirmation
      } catch (clipboardErr) {
        console.error("Fallback sharing failed:", clipboardErr);
      }
    }
  };

  if (!lesson) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-6 py-20">
        <div className="p-6 bg-yellow-50 rounded-full">
          <i className="fa-solid fa-book-open text-4xl text-yellow-600"></i>
        </div>
        <p className="text-gray-500 text-center px-8">{t.tagline}</p>
        <button
          onClick={onGenerate}
          disabled={isLoading}
          className="px-8 py-3 gold-gradient text-white rounded-full font-bold shadow-lg disabled:opacity-50"
        >
          {isLoading ? <i className="fa-solid fa-spinner fa-spin"></i> : t.generateNew}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex justify-between items-center mb-4">
        <span className="text-xs font-bold uppercase tracking-wider text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
          {lesson.theme}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">
            {new Date(lesson.date).toLocaleDateString()}
          </span>
          <button 
            onClick={handleShare}
            className="p-2 text-gray-400 hover:text-blue-500 transition-colors"
            title={t.share}
          >
            <i className="fa-solid fa-share-nodes"></i>
          </button>
          <button 
            onClick={() => onToggleFavorite(lesson.id)}
            className={`transition-colors p-2 rounded-full ${lesson.isFavorite ? 'text-yellow-500 bg-yellow-50' : 'text-gray-300 hover:text-yellow-400'}`}
          >
            <i className={`fa-solid fa-star ${lesson.isFavorite ? 'scale-110' : ''}`}></i>
          </button>
        </div>
      </div>

      <h2 className="text-3xl font-serif font-bold text-gray-900 leading-tight">
        {lesson.title}
      </h2>

      <div className="bg-white border rounded-3xl p-6 shadow-sm relative overflow-hidden">
        <div className="absolute top-0 right-0 p-3 opacity-10">
          <i className="fa-solid fa-quote-right text-4xl text-yellow-600"></i>
        </div>
        <p className="italic text-gray-700 leading-relaxed mb-4">
          "{lesson.extract}"
        </p>
        <div className="text-sm font-bold text-yellow-700 flex items-center gap-2">
           <i className="fa-solid fa-scroll"></i>
           {t.source}: {lesson.source} - {lesson.reference}
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-xl font-bold flex items-center gap-2">
          <i className="fa-solid fa-lightbulb text-yellow-500"></i>
          {t.wealth} & {t.discipline}
        </h3>
        <p className="text-gray-600 leading-relaxed">
          {lesson.principle}
        </p>
      </div>

      <div className="bg-yellow-50 rounded-2xl p-5 border border-yellow-100">
        <h3 className="font-bold text-yellow-900 mb-2 flex items-center gap-2">
          <i className="fa-solid fa-bolt"></i>
          {t.actionToTake}
        </h3>
        <p className="text-yellow-800">{lesson.action}</p>
      </div>

      <div className="bg-gray-900 rounded-2xl p-6 text-white shadow-xl">
        <h3 className="text-sm font-bold uppercase tracking-widest text-yellow-500 mb-3 flex items-center gap-2">
          <i className="fa-solid fa-hands-praying"></i>
          {t.powerfulPrayer}
        </h3>
        <p className="text-lg font-serif italic leading-relaxed text-gray-100">
          "{lesson.prayer}"
        </p>
      </div>

      {/* Notes Section */}
      <div className="pt-4 border-t border-gray-100">
        <h3 className="text-sm font-bold uppercase text-gray-400 mb-3 flex items-center gap-2">
          <i className="fa-solid fa-pen-to-square"></i>
          {t.addNote}
        </h3>
        <textarea
          value={localNotes}
          onChange={(e) => setLocalNotes(e.target.value)}
          onBlur={handleSaveNotes}
          placeholder="..."
          className="w-full min-h-[100px] p-4 bg-gray-50 rounded-2xl border-none focus:ring-1 focus:ring-yellow-200 text-sm text-gray-600 resize-none outline-none"
        />
        <div className="flex justify-end mt-2">
            <button 
              onClick={handleSaveNotes}
              className="text-[10px] font-bold uppercase tracking-widest text-yellow-600 hover:text-yellow-700"
            >
              {isSaving ? <i className="fa-solid fa-check mr-1"></i> : null}
              {isSaving ? t.noteSaved : t.saveNote}
            </button>
        </div>
      </div>

      <button
        onClick={onGenerate}
        disabled={isLoading}
        className="w-full py-4 text-gray-400 text-sm flex items-center justify-center gap-2 hover:text-yellow-600 transition-colors"
      >
        {isLoading ? <i className="fa-solid fa-spinner fa-spin"></i> : <><i className="fa-solid fa-rotate"></i> {t.generateNew}</>}
      </button>
    </div>
  );
};

export default LessonView;
